import React, { useState, useEffect, useCallback } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { InputSwitch } from "primereact/inputswitch";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { useMsal } from "@azure/msal-react";
import {
  apiService,
  HourAnalysisEntry,
  HourAnalysisGroup,
} from "../services/apiService";
import { useApiAuth } from "../hooks/useApiAuth";
import { getPreferredMsalAccount } from "../utils/msalAccountUtils";
import { useToast } from "../contexts/ToastContext";
import { WeekPicker } from "./WeekPicker";
import { Loading } from "./Loading";
import { ActivityBadge, ReferenceBadge, HourType } from "./Badges";
import { isAdmin } from "../utils/authUtils";
import { formatWeek, getCurrentWeek } from "../utils/weekUtils";

interface EmployeeData {
  person_id: number;
  name: string;
  availability: number;
  planned_hours: number;
  actual_hours: number;
  timesheet_complete: boolean;
  projects: HourAnalysisEntry[];
}

interface TimesheetAnalysisProps {
  className?: string;
}

// Helper function to get previous week
const getPreviousWeek = (): { weekNumber: number; year: number } => {
  const currentWeek = getCurrentWeek();
  const currentYear = new Date().getFullYear();

  if (currentWeek > 1) {
    return { weekNumber: currentWeek - 1, year: currentYear };
  } else {
    // If current week is 1, go to week 52 of previous year
    return { weekNumber: 52, year: currentYear - 1 };
  }
};

const TimesheetAnalysis: React.FC<TimesheetAnalysisProps> = ({ className }) => {
  // Get previous week as default
  const previousWeek = getPreviousWeek();

  const [selectedWeek, setSelectedWeek] = useState<number | undefined>(
    previousWeek.weekNumber,
  );
  const [selectedYear, setSelectedYear] = useState<number>(previousWeek.year);
  const [employeeData, setEmployeeData] = useState<EmployeeData[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<any>({});
  const [expandAll, setExpandAll] = useState(false);

  const { isApiReady } = useApiAuth();
  const { accounts, instance } = useMsal();
  const toast = useToast();

  // Check if current user has admin role
  const userIsAdmin = isAdmin(getPreferredMsalAccount(instance, accounts));

  const loadHourAnalysis = useCallback(
    async (week: number, year: number) => {
      if (!week || !year || !isApiReady) return;

      // Format week as YYYYWW
      const weekKey = formatWeek(year, week);

      try {
        setLoading(true);
        const response = await apiService.getHourAnalysis(weekKey);

        if (response.success && response.data) {
          const { data: entries, groups } = response.data;

          // Group entries by person
          const employeeMap = new Map<number, EmployeeData>();

          // Initialize employees from groups
          groups.forEach((group: HourAnalysisGroup) => {
            employeeMap.set(group.person_id, {
              person_id: group.person_id,
              name: group.name,
              availability: group.availability,
              planned_hours: 0,
              actual_hours: 0,
              timesheet_complete: group.timesheet_complete,
              projects: [],
            });
          });

          // Add entries to employees and calculate totals
          entries.forEach((entry: HourAnalysisEntry) => {
            const employee = employeeMap.get(entry.person_id);
            if (employee) {
              employee.projects.push(entry);
              employee.planned_hours += entry.planned || 0;
              employee.actual_hours += entry.actuals || 0;
            }
          });

          setEmployeeData(Array.from(employeeMap.values()));
        } else {
          toast.showError("Error", "Failed to load hour analysis data");
        }
      } catch (error) {
        console.error("Failed to load hour analysis:", error);
        toast.showError("Error", "Failed to load hour analysis data");
      } finally {
        setLoading(false);
      }
    },
    [isApiReady, toast],
  );

  useEffect(() => {
    if (selectedWeek && selectedYear) {
      loadHourAnalysis(selectedWeek, selectedYear);
    }
  }, [selectedWeek, selectedYear, loadHourAnalysis]);

  useEffect(() => {
    if (expandAll) {
      const allExpanded: any = {};
      employeeData.forEach((employee) => {
        allExpanded[employee.person_id] = true;
      });
      setExpandedRows(allExpanded);
    } else {
      setExpandedRows({});
    }
  }, [expandAll, employeeData]);

  const formatHours = (hours: number): string => {
    return hours.toFixed(2);
  };

  const formatHoursWithComparison = (
    actual: number,
    planned: number,
  ): React.ReactElement => {
    const difference = actual - planned;
    const percentage = planned > 0 ? (difference / planned) * 100 : 0;

    let style: React.CSSProperties = {};
    let icon = "";

    if (Math.abs(difference) < 0.1) {
      // Nearly equal (within 0.1 hours)
      style = { color: "#22c55e" }; // green-500
      icon = "pi pi-check";
    } else if (difference > 0) {
      // Over planned
      style = { color: "#f97316" }; // orange-500
      icon = "pi pi-arrow-up";
    } else {
      // Under planned
      style = { color: "#3b82f6" }; // blue-500
      icon = "pi pi-arrow-down";
    }

    return (
      <div className="flex align-items-center justify-content-center gap-1">
        <span>{formatHours(actual)}</span>
        <i
          className={`${icon} text-xs`}
          style={style}
          title={`${difference >= 0 ? "+" : ""}${formatHours(
            difference,
          )} hours (${percentage >= 0 ? "+" : ""}${percentage.toFixed(1)}%)`}
        ></i>
      </div>
    );
  };

  const completionBodyTemplate = (employee: EmployeeData) => {
    return (
      <Tag
        severity={employee.timesheet_complete ? "success" : "warning"}
        value={employee.timesheet_complete ? "Complete" : "Pending"}
        icon={employee.timesheet_complete ? "pi pi-check" : "pi pi-clock"}
      />
    );
  };

  const actionBodyTemplate = (employee: EmployeeData) => {
    // Only show reopen button for completed timesheets and if user is admin
    if (!employee.timesheet_complete || !userIsAdmin) {
      return <span className="text-500">-</span>;
    }

    return (
      <Button
        icon="pi pi-unlock"
        className="p-button-rounded p-button-outlined p-button-sm"
        tooltip="Reopen Timesheet"
        onClick={() => handleReopenTimesheet(employee.person_id)}
      />
    );
  };

  const handleWeekChange = (weekNumber: number, year: number) => {
    setSelectedWeek(weekNumber);
    setSelectedYear(year);
  };

  const handleReopenTimesheet = async (personId: number) => {
    if (!selectedWeek || !selectedYear) {
      toast.showError("Error", "No week selected");
      return;
    }

    const employee = employeeData.find((emp) => emp.person_id === personId);
    const employeeName = employee ? employee.name : "this employee";

    confirmDialog({
      message: `Are you sure you want to reopen the timesheet for ${employeeName}? This will allow them to make changes to their completed timesheet.`,
      header: "Confirm Timesheet Reopen",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        // Format week as YYYYWW
        const weekKey = formatWeek(selectedYear, selectedWeek);

        try {
          const response = await apiService.reopenTimesheet(weekKey, personId);

          if (response.success) {
            toast.showSuccess(
              "Success",
              "Timesheet has been reopened successfully",
            );
            // Reload the data to reflect the change
            await loadHourAnalysis(selectedWeek, selectedYear);
          } else {
            toast.showError(
              "Error",
              response.message || "Failed to reopen timesheet",
            );
          }
        } catch (error) {
          console.error("Failed to reopen timesheet:", error);
          toast.showError("Error", "Failed to reopen timesheet");
        }
      },
    });
  };

  const rowExpansionTemplate = (employee: EmployeeData) => {
    return (
      <DataTable value={employee.projects} className="p-datatable-sm">
        <Column
          field="project"
          header="Project"
          body={(entry) => (
            <div>
              <div className="font-semibold">
                {entry.client} - {entry.project}
              </div>
              <div className="flex flex-wrap gap-2 mt-1">
                <ActivityBadge
                  value={entry.activity}
                  hourType={entry.hour_type as HourType}
                  size="normal"
                />
                <ReferenceBadge value={entry.reference} size="normal" />
              </div>
            </div>
          )}
        />
        <Column
          field="planned"
          header="Planned Hours"
          body={(entry) => formatHours(entry.planned || 0)}
          style={{ width: "120px", textAlign: "center" }}
        />
        <Column
          field="actuals"
          header="Actual Hours"
          body={(entry) =>
            formatHoursWithComparison(
              entry.actuals || 0,
              entry.planned || 0,
            )
          }
          style={{ width: "120px", textAlign: "center" }}
        />
        <Column
          field="hour_type"
          header="Type"
          body={(entry) => (
            <Tag
              severity={
                entry.hour_type === "billable"
                  ? "success"
                  : entry.hour_type === "non_billable"
                    ? "info"
                    : "secondary"
              }
              value={entry.hour_type.replace("_", " ")}
            />
          )}
          style={{ width: "100px" }}
        />
        <Column
          field="comment"
          header="Comments"
          style={{ minWidth: "200px" }}
        />
      </DataTable>
    );
  };

  if (!isApiReady) {
    return (
      <Card title="Timesheet Analysis">
        <div className="flex justify-content-center">
          <div>Loading authentication...</div>
        </div>
      </Card>
    );
  }

  return (
    <div className={`grid ${className || ""}`}>
      {/* Left Side - Controls */}
      <div className="col-12 md:col-3">
        <Card title="Controls" className="h-full">
          <div className="space-y-4">
            {/* Week Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Select Week
              </label>
              <WeekPicker
                value={selectedWeek}
                year={selectedYear}
                onChange={handleWeekChange}
              />
            </div>

            {/* Expand/Collapse All */}
            <div className="flex align-items-center gap-2">
              <InputSwitch
                checked={expandAll}
                onChange={(e) => setExpandAll(e.value)}
              />
              <label className="text-sm">
                {expandAll ? "Collapse All" : "Expand All"}
              </label>
            </div>
          </div>
        </Card>
      </div>

      {/* Right Side - Analysis Table */}
      <div className="col-12 md:col-9">
        <Card
          title={`Timesheet Analysis${
            selectedWeek ? ` - Week ${selectedWeek}/${selectedYear}` : ""
          }`}
        >
          {loading ? (
            <Loading message="Loading hour analysis..." />
          ) : !selectedWeek ? (
            <div className="text-center text-500 py-4">
              Please select a week to view the analysis
            </div>
          ) : employeeData.length === 0 ? (
            <div className="text-center text-500 py-4">
              No timesheet data found for the selected week
            </div>
          ) : (
            <DataTable
              value={employeeData}
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data)}
              rowExpansionTemplate={rowExpansionTemplate}
              dataKey="person_id"
              className="p-datatable-sm"
            >
              <Column expander style={{ width: "3em" }} />
              <Column
                field="name"
                header="Employee"
                style={{ minWidth: "150px" }}
              />
              <Column
                field="planned_hours"
                header="Planned Hours"
                body={(employee) => formatHours(employee.planned_hours)}
                style={{ width: "120px", textAlign: "center" }}
              />
              <Column
                field="actual_hours"
                header="Actual Hours"
                body={(employee) =>
                  formatHoursWithComparison(
                    employee.actual_hours,
                    employee.planned_hours,
                  )
                }
                style={{ width: "120px", textAlign: "center" }}
              />
              <Column
                field="timesheet_complete"
                header="Status"
                body={completionBodyTemplate}
                style={{ width: "120px" }}
              />
              <Column
                field="availability"
                header="Available Hours"
                body={(employee) => formatHours(employee.availability)}
                style={{ width: "120px", textAlign: "center" }}
              />
              {userIsAdmin && (
                <Column
                  header="Actions"
                  body={actionBodyTemplate}
                  style={{ width: "80px" }}
                />
              )}
            </DataTable>
          )}
        </Card>
      </div>
      <ConfirmDialog />
    </div>
  );
};

export default TimesheetAnalysis;
